/**
 * lrb-params.js — the email parameter contract
 * Leaps & Rebounds landing pages
 *
 * Liquid cannot read query parameters, so every parameter-driven decision on a
 * landing page happens here. This file is deliberately independent of
 * lrb-personalization.js: the offer copy must survive a cold Railway container,
 * a failed sections request, or the personalization layer not being on the page
 * at all. It touches no network.
 *
 * The contract, as the email marketer writes it:
 *   ?lrb_offer=storecredit        the slug sections target on
 *   ?lrb_offer_text=$35 off       the offer as the shopper reads it
 *   ?lrb_code=BOUNCE35            shown, and applied by the button
 *   ?lrb_ends=2026-09-30          starts the countdown (store time, end of day)
 *   ?lrb_angle=warranty           ranks the matching proof sections up
 *
 * There is deliberately no headline override: the hero eyebrow carries
 * lrb_offer_text, and a parameter nothing reads is a trap for whoever writes the
 * link. Add one here only together with the markup that honours it.
 *
 * The markup contract, as the snippets use it:
 *   data-lrb-var="lrb_code"                     text replaced with the value
 *   data-lrb-show="lrb_offer=storecredit,freeship"   kept only when it matches
 *   data-lrb-hide="lrb_offer"                   dropped when that param is present
 *   data-lrb-countdown                          ticks down to lrb_ends
 *   data-lrb-urgency                            the block a dead countdown removes
 *   data-lrb-code-apply                         link keeps its href and gains ?discount=<code>
 *
 * Every parameter is optional. With none of them the page must still read as a
 * coherent page, because links get forwarded and mail clients strip query
 * strings. Values are attacker-controllable, so text is written with
 * textContent and the discount code is character-restricted.
 */

(function () {
  'use strict';

  if (window.LRB && window.LRB.applyOfferCopy) return; // already loaded

  var PARAM_KEYS = ['lrb_offer', 'lrb_offer_text', 'lrb_angle', 'lrb_code', 'lrb_ends'];

  // Anyone can craft a link, so a value that lands in shopper-visible copy is
  // capped. It cannot stop someone writing a wrong offer into a URL, but it does
  // stop a pasted essay from taking the layout apart.
  var MAX_TEXT = 120;

  // lrb_ends is read in the store's timezone, so an offer ends at the same
  // moment for every shopper. Keep in step with Settings → General → Time zone.
  var STORE_TZ = 'America/Los_Angeles';

  // A countdown filled into a clone that never reaches the page is dropped
  // after this long, so it does not tick forever.
  var DETACHED_GRACE_MS = 10000;

  var params = (function () {
    var qs = new URLSearchParams(window.location.search);
    var out = {};
    PARAM_KEYS.forEach(function (k) {
      var v = (qs.get(k) || '').trim();
      if (v) out[k] = v;
    });
    return out;
  })();

  // An expired link reads as a link with no offer, so the offer band stays
  // hidden instead of advertising a code checkout will reject.
  // lrb_angle is targeting, not offer, so it survives.
  var deadline = parseDeadline(params.lrb_ends);
  if (deadline && deadline.getTime() <= Date.now()) {
    ['lrb_offer', 'lrb_offer_text', 'lrb_code', 'lrb_ends'].forEach(function (k) {
      delete params[k];
    });
    deadline = null;
  }

  var countdowns = [];
  var timer = null;

  function paramMatches(expr) {
    // "lrb_offer=a,b" is true when lrb_offer is a or b. "lrb_offer" is true when present.
    if (!expr) return false;
    var eq = expr.indexOf('=');
    if (eq === -1) return !!params[expr.trim()];
    var key = expr.slice(0, eq).trim();
    var want = expr
      .slice(eq + 1)
      .split(',')
      .map(function (v) {
        return v.trim().toLowerCase();
      });
    var have = (params[key] || '').toLowerCase();
    return !!have && want.indexOf(have) !== -1;
  }

  function formatRemaining(ms) {
    var m = Math.floor(ms / 60000) % 60;
    var h = Math.floor(ms / 3600000) % 24;
    var d = Math.floor(ms / 86400000);
    if (d > 0) return d + 'd ' + h + 'h ' + m + 'm';
    var sec = Math.floor(ms / 1000) % 60;
    return h + 'h ' + m + 'm ' + sec + 's';
  }

  function tick() {
    var now = Date.now();
    countdowns = countdowns.filter(function (c) {
      // A clone is filled before it is appended, so it is legitimately detached
      // on the first tick. Drop it once it has been on the page and left, or if
      // it never arrived.
      if (c.el.isConnected) c.seen = true;
      else if (c.seen || now - c.added > DETACHED_GRACE_MS) return false;
      var left = c.ends - now;
      if (left <= 0) {
        // Drop the whole urgency block rather than leave a zeroed clock
        // promising a deal that has ended.
        (c.el.closest('[data-lrb-urgency]') || c.el).remove();
        return false;
      }
      c.el.textContent = formatRemaining(left);
      return true;
    });
    if (!countdowns.length && timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  // Milliseconds to add to UTC to get wall-clock time in tz at instant t.
  function tzOffset(t, tz) {
    var parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    })
      .formatToParts(new Date(t))
      .forEach(function (p) {
        parts[p.type] = +p.value;
      });
    var wall = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );
    return wall - Math.floor(t / 1000) * 1000;
  }

  // A wall-clock time in the store's timezone, as a Date. The second pass
  // corrects a guess that landed on the other side of a DST change.
  function storeTime(y, mo, d, h, mi, s) {
    var wall = Date.UTC(y, mo - 1, d, h, mi, s);
    try {
      var t = wall - tzOffset(wall, STORE_TZ);
      return new Date(wall - tzOffset(t, STORE_TZ));
    } catch {
      // No timezone support: the visitor's local time is the closest we have.
      return new Date(y, mo - 1, d, h, mi, s);
    }
  }

  function parseDeadline(raw) {
    if (!raw) return null;
    // YYYY-MM-DD, or YYYY-MM-DDTHH:MM[:SS] with no offset, is store time. A date
    // alone means the end of that day: an offer that "ends September 30" runs
    // to 23:59:59 on the 30th.
    var m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(raw.trim());
    if (m) {
      var y = +m[1],
        mo = +m[2],
        d = +m[3];
      // Reject dates that do not exist (2026-02-31) rather than letting them
      // roll over into the next month.
      var check = new Date(Date.UTC(y, mo - 1, d));
      if (check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
      if (m[4] === undefined) return storeTime(y, mo, d, 23, 59, 59);
      if (+m[4] > 23 || +m[5] > 59 || +(m[6] || 0) > 59) return null;
      return storeTime(y, mo, d, +m[4], +m[5], +(m[6] || 0));
    }
    // Anything else (an explicit offset or Z) is taken as written.
    var dt = new Date(raw);
    return isNaN(dt.getTime()) ? null : dt;
  }

  function applyOfferCopy(root) {
    if (!root || !root.querySelectorAll) return;

    // Conditionals first, so we never fill copy we are about to remove.
    root.querySelectorAll('[data-lrb-show]').forEach(function (el) {
      if (paramMatches(el.getAttribute('data-lrb-show'))) {
        // Liquid ships these hidden so nothing flashes before we decide.
        el.removeAttribute('hidden');
        el.classList.remove('lrb-hidden');
      } else {
        el.remove();
      }
    });
    root.querySelectorAll('[data-lrb-hide]').forEach(function (el) {
      if (paramMatches(el.getAttribute('data-lrb-hide'))) el.remove();
    });

    // No parameter means the Liquid fallback copy stands.
    root.querySelectorAll('[data-lrb-var]').forEach(function (el) {
      var val = params[el.getAttribute('data-lrb-var')];
      if (!val) return;
      el.textContent = val.length > MAX_TEXT ? val.slice(0, MAX_TEXT).trim() + '…' : val;
    });

    // Restricted to the characters Shopify allows in a discount URL, so a
    // hand-edited parameter cannot build a different link.
    var code = (params.lrb_code || '').replace(/[^A-Za-z0-9_-]/g, '');
    // The link keeps its own destination and gains ?discount=, which Shopify
    // turns into the discount_code cookie. /discount/<code>?redirect= would cost
    // a round trip and reset a PDP's variant (see lrb-fit-finder).
    if (code) {
      root.querySelectorAll('[data-lrb-code-apply]').forEach(function (el) {
        if (el.tagName !== 'A') return;
        try {
          var url = new URL(el.getAttribute('href') || '/', window.location.href);
          url.searchParams.set('discount', code);
          el.href =
            url.origin === window.location.origin ? url.pathname + url.search + url.hash : url.href;
        } catch {
          /* unparseable href: leave the link alone */
        }
      });
    }

    // Only an lrb_ends that parses starts a clock; anything else leaves the
    // static fallback text alone.
    var ends = deadline;
    if (ends) {
      var added = false;
      root.querySelectorAll('[data-lrb-countdown]').forEach(function (el) {
        // Without this a screen reader announces the countdown once a second.
        el.setAttribute('role', 'timer');
        el.setAttribute('aria-live', 'off');
        countdowns.push({ el: el, ends: ends.getTime(), added: Date.now() });
        added = true;
      });
      if (added) {
        tick();
        if (countdowns.length && !timer) timer = setInterval(tick, 1000);
      }
    }
  }

  window.LRB = window.LRB || {};
  window.LRB.PARAM_KEYS = PARAM_KEYS;
  window.LRB.params = params;
  window.LRB.paramMatches = paramMatches;
  window.LRB.applyOfferCopy = applyOfferCopy;

  function runOnce() {
    applyOfferCopy(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runOnce);
  } else {
    runOnce();
  }
})();
